import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { SERVICES, PROBLEMS, INDUSTRIES, RESOURCES, INSIGHTS } from "../data/site";

const SEARCH_INDEX = [
  ...SERVICES.map((s) => ({
    group: "Services",
    title: s.name,
    subtitle: s.positioning,
    to: `/services/${s.slug}`,
  })),
  {
    group: "Assessments",
    title: "General Technology Health Assessment",
    subtitle: "A top-to-bottom review of your technology, spend, and risk.",
    to: "/assessments/it-assessment",
  },
  {
    group: "Assessments",
    title: "AI Readiness Assessment",
    subtitle: "Evaluate whether your data, processes, and governance are AI-ready.",
    to: "/assessments/ai-readiness",
  },
  {
    group: "Assessments",
    title: "Cybersecurity Assessment",
    subtitle: "See your real cyber exposure — without the fear or jargon.",
    to: "/assessments/cybersecurity",
  },
  ...PROBLEMS.map((p) => ({
    group: "Problems We Solve",
    title: p.title,
    subtitle: p.angle,
    to: `/problems/${p.slug}`,
  })),
  ...INDUSTRIES.map((i) => ({
    group: "Industries",
    title: i.name,
    subtitle: i.summary,
    to: `/industries/${i.slug}`,
  })),
  ...RESOURCES.filter((r) => !r.comingSoon && r.type !== "Article").map((r) => ({
    group: "Free Resources",
    title: r.title,
    subtitle: r.summary,
    to: "/resources",
  })),
  ...INSIGHTS.map((a) => ({
    group: "Insights",
    title: a.title,
    subtitle: a.excerpt,
    to: `/insights/${a.slug}`,
  })),
  { group: "Pages", title: "Our Methodology", subtitle: "Our six-phase, business-first approach.", to: "/methodology" },
  { group: "Pages", title: "Pricing & Engagement Models", subtitle: "How we engage and how pricing works.", to: "/pricing" },
  { group: "Pages", title: "Case Studies & Outcomes", subtitle: "Proof, not promises.", to: "/case-studies" },
  { group: "Pages", title: "About Us", subtitle: "Who we are and how we work.", to: "/about" },
  { group: "Pages", title: "Contact / Schedule a Technology Health Assessment", subtitle: "Start a conversation.", to: "/contact" },
];

const GROUPS = ["Services", "Assessments", "Problems We Solve", "Industries", "Free Resources", "Insights", "Pages"];

export const SiteSearch = ({ open, onOpenChange }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Search" description="Search the Silvertree Solutions site">
      <CommandInput placeholder="Search services, problems, resources…" data-testid="site-search-input" />
      <CommandList data-testid="site-search-results">
        <CommandEmpty>No results found.</CommandEmpty>
        {GROUPS.map((g) => (
          <CommandGroup key={g} heading={g}>
            {SEARCH_INDEX.filter((it) => it.group === g).map((it) => (
              <CommandItem
                key={`${g}-${it.title}`}
                value={`${it.title} ${it.subtitle}`}
                onSelect={() => go(it.to)}
                data-testid={`search-result-${it.to.replace(/\//g, "-").slice(1) || "home"}-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="cursor-pointer"
              >
                <div>
                  <p className="text-sm font-medium">{it.title}</p>
                  <p className="text-xs opacity-60 line-clamp-1">{it.subtitle}</p>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export const useSiteSearch = () => {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
};
